/**
 * Onglet Suivis — les règles de tri et de filtrage des comptes suivis. (§D49)
 *
 * L'objectif produit : repérer les comptes que Vincent suit et qui ne
 * produisent plus rien, pour qu'il aille les unfollow **dans X**. L'app ne
 * touche à rien — elle ouvre le profil, point.
 *
 * Deux axes de dormance, indépendants :
 *   - `last_activity_at` — le dernier tweet du compte, déduit du snowflake de
 *     `most_recent_tweet_id` (gratuit, cf. `snowflake.ts`) ;
 *   - `last_interaction_at` — la dernière fois que *moi* j'ai interagi avec ce
 *     compte (reply loggée ou interaction Orbit).
 *
 * Et depuis le 14/09/2026 (§D51), un troisième signal qui ne remplace pas le
 * premier mais le complète : `last_liked_post_at`, le post le plus récemment
 * liké par ce compte. Il coûte cher et se demande à la main, d'où le couple
 * « valeur + date de vérification » ci-dessous.
 *
 * Tout est pur : `now` est un argument, jamais une horloge implicite.
 */
export const EMPTY_FOLLOWING_SYNC = {
    pagination_token: null,
    synced_count: 0,
    last_sync_at: null,
    completed_at: null,
    last_error: null,
    sync_generation: 0,
};
export const ACTIVITY_FILTERS = [
    { key: "all", label: "Tous", months: null },
    { key: "1m", label: "> 1 mois", months: 1 },
    { key: "3m", label: "> 3 mois", months: 3 },
    { key: "6m", label: "> 6 mois", months: 6 },
    { key: "1y", label: "> 1 an", months: 12 },
    { key: "2y", label: "> 2 ans", months: 24 },
    { key: "never", label: "Jamais vue", months: null },
];
export const INTERACTION_FILTERS = [
    { key: "all", label: "Toutes", months: null },
    { key: "never", label: "Jamais", months: null },
    { key: "3m", label: "> 3 mois", months: 3 },
    { key: "6m", label: "> 6 mois", months: 6 },
    { key: "1y", label: "> 1 an", months: 12 },
];
/**
 * L'instant situé N mois avant `now`.
 *
 * Arithmétique calendaire UTC : un seuil de dormance n'a pas besoin de la
 * précision d'un fuseau, et le débordement de fin de mois (31 mars − 1 mois →
 * 3 mars) déplace la frontière de deux jours au pire — invisible sur une
 * question qui se pose en mois.
 */
export function monthsBefore(now, months) {
    const d = new Date(now.getTime());
    d.setUTCMonth(d.getUTCMonth() - months);
    return d;
}
function olderThan(at, months, now) {
    if (months === null)
        return true;
    // Un compte jamais vu actif est plus dormant que n'importe quel seuil : il
    // doit apparaître dans « > 2 ans » comme dans « > 1 mois ».
    if (at === null)
        return true;
    return at.getTime() <= monthsBefore(now, months).getTime();
}
export function matchesActivityFilter(lastActivityAt, filter, now) {
    if (filter === "all")
        return true;
    if (filter === "never")
        return lastActivityAt === null;
    const option = ACTIVITY_FILTERS.find((f) => f.key === filter);
    return olderThan(lastActivityAt, option?.months ?? null, now);
}
export function matchesInteractionFilter(lastInteractionAt, filter, now) {
    if (filter === "all")
        return true;
    if (filter === "never")
        return lastInteractionAt === null;
    const option = INTERACTION_FILTERS.find((f) => f.key === filter);
    return olderThan(lastInteractionAt, option?.months ?? null, now);
}
/**
 * Les options de filtre qui ont un sens pour un compte de cet âge.
 *
 * Proposer « > 2 ans » à quelqu'un dont le compte X a dix-huit mois annonce un
 * filtre qui ne rendra jamais rien. On borne donc la liste par la date de
 * création du compte — quand on la connaît. `null` ⇒ aucune borne, toutes les
 * options restent offertes.
 */
export function boundedFilters(options, accountCreatedAt, now) {
    if (accountCreatedAt === null)
        return options;
    return options.filter((option) => option.months === null ||
        monthsBefore(now, option.months).getTime() >= accountCreatedAt.getTime());
}
/**
 * Tri fixe de la grille : **le plus dormant en premier**.
 *
 * Les comptes sans activité connue passent devant tout le monde — ce sont les
 * candidats les plus probables à l'unfollow, et ce sont aussi ceux qu'un tri
 * par date pousserait tout en bas si on les traitait comme « date zéro ».
 */
export function compareDormancy(a, b) {
    if (a.last_activity_at === null && b.last_activity_at === null)
        return 0;
    if (a.last_activity_at === null)
        return -1;
    if (b.last_activity_at === null)
        return 1;
    return Date.parse(a.last_activity_at) - Date.parse(b.last_activity_at);
}
/** Le signal du mode courant, pour un compte donné. */
export function signalFor(account, mode) {
    if (mode === "posts")
        return { at: account.last_activity_at, known: true };
    return {
        at: account.last_liked_post_at,
        known: account.likes_checked_at !== null,
    };
}
/**
 * Le tri de la grille, dans le mode courant.
 *
 * L'ordre, du haut vers le bas :
 *   1. les **non vérifiés** (mode Likes seulement) — ce sont eux que le bouton
 *      « Vérifier les likes » va lire, donc ils doivent être sous la main ;
 *   2. les comptes **sans date connue** malgré la vérification ;
 *   3. les autres, **du plus dormant au plus récent**.
 */
export function compareByMode(a, b, mode) {
    const sa = signalFor(a, mode);
    const sb = signalFor(b, mode);
    if (sa.known !== sb.known)
        return sa.known ? 1 : -1;
    if (sa.at === null && sb.at === null)
        return 0;
    if (sa.at === null)
        return -1;
    if (sb.at === null)
        return 1;
    return Date.parse(sa.at) - Date.parse(sb.at);
}
/**
 * Taille de page demandée à `/2/users/:id/liked_tweets`.
 *
 * C'est le **minimum** accepté par l'endpoint, pas un choix de confort : on ne
 * veut que le premier élément (le plus récemment liké), mais X refuse en 400
 * en dessous de 5. On paye donc cinq posts pour en lire un — $0,025 le compte.
 */
export const LIKES_PAGE_SIZE = 5;
/**
 * Comptes vérifiables par déclenchement.
 *
 * Ce n'est pas le budget qui borne ici mais la **cadence** de l'endpoint,
 * limitée à ~75 requêtes par fenêtre de 15 minutes. Au-delà, X répond 429 et
 * le lot s'arrête pour rien. L'UI dit alors « N vérifiés — relance pour les
 * suivants ».
 */
export const LIKES_BATCH_MAX = 75;
/**
 * Ce que coûtera un lot, en USD.
 *
 * Affiché AVANT la confirmation : c'est la seule chose qui rende ce bouton
 * honnête. Les posts likés sont des tweets d'autrui, donc classe `post` — la
 * classe `owned` ne vaut que quand l'id lu est le mien (cf. docs/X-API.md).
 */
export function estimateLikesCheckCost(accounts, postUnitCost) {
    return Math.round(accounts * LIKES_PAGE_SIZE * postUnitCost * 1e6) / 1e6;
}
export const RECIPROCITY_FILTERS = [
    { key: "all", label: "Tous", months: null },
    { key: "follows_me", label: "Me suivent", months: null },
    { key: "not_following_me", label: "Ne me suivent pas", months: null },
];
/**
 * Le filtre de réciprocité.
 *
 * **`null` n'est pas un « non ».** Un compte jamais croisé avec ma liste de
 * followers est un inconnu, et il ne doit apparaître ni dans « me suivent » ni
 * dans « ne me suivent pas » : sinon la liste d'unfollow — le cas d'usage roi
 * de cette page — contiendrait des gens qui me suivent peut-être. On ne
 * recommande pas un geste irréversible sur une donnée qu'on n'a pas.
 */
export function matchesReciprocityFilter(followsMe, filter) {
    if (filter === "all")
        return true;
    if (followsMe === null)
        return false;
    return filter === "follows_me" ? followsMe : !followsMe;
}
//# sourceMappingURL=following.js.map